import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
    Building2, MapPin, Phone, Mail, Users, Package,
    DollarSign, Plus, Edit, Trash2, UserPlus, ChevronRight
} from 'lucide-react';

const EstablishmentsPage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [establishments, setEstablishments] = useState([]);
    const [users, setUsers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingEstablishment, setEditingEstablishment] = useState(null);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedEstablishment, setSelectedEstablishment] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        is_active: true
    });
    
    const isAdmin = user?.role === 'ADMIN';
    
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch establishments first
            const estRes = await api().get('/establishments');
            setEstablishments(estRes.data);
            
            // Fetch users separately to avoid blocking establishments on user fetch error
            try {
                const usersRes = await api().get('/admin/users');
                setUsers(usersRes.data);
            } catch (userError) {
                console.error('Error fetching users:', userError);
                // Don't block establishments display if users fail
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching establishments:', error);
            if (error.response?.status !== 403) {
                toast.error('Erreur lors du chargement des établissements');
            }
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Le nom est requis');
            return;
        }
        
        try {
            if (editingEstablishment) {
                await api().put(`/establishments/${editingEstablishment.id}`, formData);
                toast.success('Établissement modifié');
            } else {
                await api().post('/establishments', formData);
                toast.success('Établissement créé');
            }
            setDialogOpen(false);
            setEditingEstablishment(null);
            setFormData({ name: '', address: '', phone: '', email: '', is_active: true });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleDelete = async (id) => {
        if (!confirm('Supprimer cet établissement ?')) return;
        
        try {
            await api().delete(`/establishments/${id}`);
            toast.success('Établissement supprimé');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleAssignUser = async () => {
        if (!selectedUserId || !selectedEstablishment) return;
        
        try {
            await api().post(`/establishments/${selectedEstablishment.id}/assign-user?user_id=${selectedUserId}`);
            toast.success('Utilisateur assigné');
            setAssignDialogOpen(false);
            setSelectedUserId('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const openEditDialog = (est) => {
        setEditingEstablishment(est);
        setFormData({
            name: est.name,
            address: est.address || '',
            phone: est.phone || '',
            email: est.email || '',
            is_active: est.is_active
        });
        setDialogOpen(true);
    };
    
    const openCreateDialog = () => {
        setEditingEstablishment(null);
        setFormData({ name: '', address: '', phone: '', email: '', is_active: true });
        setDialogOpen(true);
    };
    
    if (!isAdmin && user?.role !== 'DIRECTEUR') {
        return (
            <div className="p-6 flex items-center justify-center h-64">
                <p className="text-muted-foreground">Accès non autorisé</p>
            </div>
        );
    }
    
    return (
        <div className="p-6 space-y-6" data-testid="establishments-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Building2 className="w-6 h-6 text-blue-600" />
                        Établissements
                    </h1>
                    <p className="text-muted-foreground">
                        Gérez vos différents points de vente
                    </p>
                </div>
                {isAdmin && (
                    <Button onClick={openCreateDialog}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvel établissement
                    </Button>
                )}
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Total établissements</CardDescription>
                        <CardTitle className="text-2xl">{establishments.length}</CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Établissements actifs</CardDescription>
                        <CardTitle className="text-2xl text-green-600">
                            {establishments.filter(e => e.is_active).length}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>CA Total du jour</CardDescription>
                        <CardTitle className="text-2xl text-amber-600">
                            {establishments.reduce((sum, e) => sum + (e.today_revenue || 0), 0).toLocaleString()} CFA
                        </CardTitle>
                    </CardHeader>
                </Card>
            </div>
            
            {/* Establishments List */}
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
                </div>
            ) : establishments.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground mb-4">Aucun établissement créé</p>
                        {isAdmin && (
                            <Button onClick={openCreateDialog}>
                                <Plus className="w-4 h-4 mr-2" />
                                Créer le premier établissement
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {establishments.map(est => (
                        <Card key={est.id} className={!est.is_active ? 'opacity-60' : ''}>
                            <CardHeader className="pb-2">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            {est.name}
                                            {!est.is_active && (
                                                <Badge variant="secondary">Inactif</Badge>
                                            )}
                                        </CardTitle>
                                        <CardDescription className="flex items-center gap-1 mt-1">
                                            <MapPin className="w-3 h-3" />
                                            {est.address || 'Adresse non définie'}
                                        </CardDescription>
                                    </div>
                                    {isAdmin && (
                                        <div className="flex gap-1">
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={() => openEditDialog(est)}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                className="text-red-500"
                                                onClick={() => handleDelete(est.id)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Contact Info */}
                                <div className="flex gap-4 text-sm text-muted-foreground">
                                    {est.phone && (
                                        <span className="flex items-center gap-1">
                                            <Phone className="w-3 h-3" />
                                            {est.phone}
                                        </span>
                                    )}
                                    {est.email && (
                                        <span className="flex items-center gap-1">
                                            <Mail className="w-3 h-3" />
                                            {est.email}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Stats */}
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded text-center">
                                        <p className="text-xs text-muted-foreground">Produits</p>
                                        <p className="font-bold flex items-center justify-center gap-1">
                                            <Package className="w-3 h-3" />
                                            {est.products_count || 0}
                                        </p>
                                    </div>
                                    <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded text-center">
                                        <p className="text-xs text-muted-foreground">Personnel</p>
                                        <p className="font-bold flex items-center justify-center gap-1">
                                            <Users className="w-3 h-3" />
                                            {est.users_count || 0}
                                        </p>
                                    </div>
                                    <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-center">
                                        <p className="text-xs text-muted-foreground">CA Jour</p>
                                        <p className="font-bold text-amber-600 flex items-center justify-center gap-1">
                                            <DollarSign className="w-3 h-3" />
                                            {((est.today_revenue || 0) / 1000).toFixed(0)}k
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Actions */}
                                {isAdmin && (
                                    <Button 
                                        variant="outline" 
                                        className="w-full"
                                        onClick={() => {
                                            setSelectedEstablishment(est);
                                            setAssignDialogOpen(true);
                                        }}
                                    >
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Assigner un employé
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
            
            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingEstablishment ? 'Modifier l\'établissement' : 'Nouvel établissement'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="est-name">Nom *</Label>
                            <Input
                                id="est-name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Restaurant Centre-Ville"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="est-address">Adresse</Label>
                            <Input
                                id="est-address"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder="123 Rue Example"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="est-phone">Téléphone</Label>
                                <Input
                                    id="est-phone"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="+241 XX XX XX XX"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="est-email">Email</Label>
                                <Input
                                    id="est-email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="contact@restaurant.com"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <Label htmlFor="est-active">Établissement actif</Label>
                            <Switch
                                id="est-active"
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave}>
                            {editingEstablishment ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Assign User Dialog */}
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Assigner un employé</DialogTitle>
                        <DialogDescription>
                            Assigner un employé à {selectedEstablishment?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sélectionner un employé" />
                            </SelectTrigger>
                            <SelectContent>
                                {users.map(u => (
                                    <SelectItem key={u.id} value={u.id}>
                                        {u.name} ({u.role})
                                        {u.establishment_name && (
                                            <span className="text-muted-foreground ml-2">
                                                - {u.establishment_name}
                                            </span>
                                        )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleAssignUser} disabled={!selectedUserId}>
                            Assigner
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EstablishmentsPage;
